import {
  AsyncThunk,
  AsyncThunkOptions,
  AsyncThunkPayloadCreator,
  createAsyncThunk as reduxCreateAsyncThunk,
} from '@reduxjs/toolkit';

import { HttpClient, IUser, ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import type { ErrorCodesMap } from '@wix/error-handler';

import { monitor } from 'controller/error-monitor';

import type {
  ITokenPayload,
  AsyncThunkConfig,
  I18NextTransProps,
} from './types';

type ThunkOptions<Arg> = AsyncThunkOptions<Arg, AsyncThunkConfig> & {
  hideErrorMessage?: boolean | ((error: unknown) => boolean);

  formatError?(arg: Arg, error: unknown): I18NextTransProps;
  errorCodesMap?: ErrorCodesMap | ((api: ControllerFlowAPI) => ErrorCodesMap);
};

type ReduxAsyncThunkConfig = {
  pendingMeta?: unknown;
  serializedErrorType?: unknown;
};

export function createAsyncThunk<
  Returned,
  Arg = void,
  Config extends ReduxAsyncThunkConfig = {},
>(
  prefix: string,
  payloadCreator: AsyncThunkPayloadCreator<Returned, Arg, AsyncThunkConfig>,
  options: ThunkOptions<Arg> = {},
) {
  const create = reduxCreateAsyncThunk.withTypes<AsyncThunkConfig>();

  return create(
    prefix,
    async function (arg, thunkApi) {
      const { dispatch, extra: flowApi } = thunkApi;
      const { withErrorHandler, getResolvedError } = flowApi.errorHandler;
      const errorMonitor = monitor(flowApi);

      const {
        hideErrorMessage = false,
        errorCodesMap = {},
        formatError = defaultErrorFormatter(prefix),
      } = options;
      const codesMap =
        typeof errorCodesMap === 'function'
          ? errorCodesMap(thunkApi.extra)
          : errorCodesMap;
      try {
        const result = await withErrorHandler(
          async () => payloadCreator(arg, thunkApi),
          {
            errorCodesMap: codesMap,
          },
        );

        return result;
      } catch (error) {
        const hideToast =
          typeof hideErrorMessage === 'function'
            ? hideErrorMessage(error)
            : hideErrorMessage;

        const resolvedError = getResolvedError(error);
        if (!hideToast) {
          dispatch({
            type: 'application/showToast',
            payload: {
              type: 'error',
              action: prefix,
              resolvedError,
              i18nParams: formatError(arg, resolvedError),
              requestId: HttpClient.isHttpError(error)
                ? error.requestId
                : undefined,
              description: HttpClient.isHttpError(error)
                ? error.message
                : undefined,
            },
          });
        }

        errorMonitor.captureException(error as Error, {
          transaction: prefix,
          tags: { thunk: true },
        });

        if (HttpClient.isHttpError(error)) {
          console.error(prefix, error.requestId, error.response?.data);
        } else {
          console.error(prefix, error);
        }

        throw error;
      }
    },
    options,
  ) as AsyncThunk<Returned, Arg, Config & AsyncThunkConfig>;
}

export function defaultErrorFormatter(name: string) {
  const key = name.replace(/:/g, '.');

  return () => ({
    i18nKey: `groups-web.toast.error.${key}`,
  });
}

export function is404(error: unknown) {
  if (!HttpClient.isHttpError(error)) {
    return false;
  }

  return error?.response?.status === 404;
}

export function getCurrentUser(user: IUser, instance?: string) {
  if (!instance) {
    return user;
  }

  const payload = parseJWT(instance);

  return {
    ...user,
    instance,
    id: user.id || payload.siteMemberId || payload.uid,
    loggedIn: user.loggedIn || !!payload.siteMemberId || !!payload.uid,
  };
}

function parseJWT(jwt: string): ITokenPayload {
  try {
    const [, payload] = jwt.split('.');
    return JSON.parse(atob(payload));
  } catch {
    return {};
  }
}
